import type {
  DraftOrder,
  DraftOrderBlocker,
  DraftOrderStatus,
} from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';

/** Completed and cancelled drafts are history; the backend answers 409 to any change. */
export function isEditable(status: DraftOrderStatus): boolean {
  return status === 'draft' || status === 'invoice_sent';
}

/** An invoiced quote went to the shopper, so it is cancelled rather than deleted. */
export function canDelete(status: DraftOrderStatus): boolean {
  return status === 'draft';
}

export function canCancel(status: DraftOrderStatus): boolean {
  return isEditable(status);
}

/**
 * What stops the invoice dialog from opening. A missing email is left out: the dialog asks for
 * a recipient, and the backend accepts `to` in its place.
 */
export function invoiceGateBlockers(
  draft: Pick<DraftOrder, 'status' | 'invoiceBlockers'>,
): DraftOrderBlocker[] {
  return isEditable(draft.status)
    ? draft.invoiceBlockers.filter((blocker) => blocker !== 'missing_email')
    : [];
}

export function needsRecipient(draft: Pick<DraftOrder, 'invoiceBlockers'>): boolean {
  return draft.invoiceBlockers.includes('missing_email');
}

const BLOCKERS: ReadonlySet<string> = new Set<DraftOrderBlocker>([
  'no_lines',
  'unavailable_line',
  'missing_email',
  'missing_shipping_address',
  'shipping_rate_unavailable',
]);

/** Blockers the backend listed on a 409 from send-invoice or complete. */
export function blockersFromConflict(error: unknown): DraftOrderBlocker[] | null {
  if (!(error instanceof ApiError) || error.kind !== 'conflict') return null;
  const blockers = error.meta.blockers;
  if (!Array.isArray(blockers)) return null;
  return blockers.filter(
    (value): value is DraftOrderBlocker => typeof value === 'string' && BLOCKERS.has(value),
  );
}

/** Any other 409: the draft was completed or cancelled meanwhile. */
export function isStateConflict(error: unknown): boolean {
  return error instanceof ApiError && error.kind === 'conflict';
}

/** `body.lines.2.customizations.0.value` → `{ index: 2, rest: 'customizations.0.value' }`. */
export function lineErrorTarget(field: string): { index: number; rest: string } | null {
  const match = /^(?:body\.)?lines\.(\d+)(?:\.(.+))?$/.exec(field);
  if (!match?.[1]) return null;
  return { index: Number(match[1]), rest: match[2] ?? '' };
}

/**
 * Where a backend field error about the line just added belongs in its dialog. `field` comes
 * without the `body.` prefix; the customization index is the position in the list that was sent.
 * Errors about other lines, or paths the dialog has no field for, return null.
 */
export function lineFieldPath(
  field: string,
  index: number,
  customizationKeys: readonly string[],
): string | null {
  const target = lineErrorTarget(field);
  if (target?.index !== index) return null;
  return linePartPath(target.rest, customizationKeys);
}

function linePartPath(rest: string, customizationKeys: readonly string[]): string | null {
  if (rest === 'variantId' || rest === 'quantity' || rest === 'title' || rest === 'sku') {
    return rest;
  }
  if (rest === 'unitPrice' || rest.startsWith('unitPrice.')) return 'unitPrice';
  const match = /^customizations\.(\d+)\.(?:value(?:\.mediaId)?|key)$/.exec(rest);
  const key = match ? customizationKeys[Number(match[1])] : undefined;
  return key ? `customizations.${key}` : null;
}

/**
 * Where a backend field error from the line PATCH belongs in the edit dialog. Body checks name
 * the field directly (`quantity`, `unitPrice.currency`, `customizations.0.value`); catalog checks
 * run over the whole list and name the line by its position (`lines.2.customizations.0.value`).
 */
export function editLineFieldPath(
  field: string,
  position: number,
  customizationKeys: readonly string[],
): string | null {
  const stripped = field.replace(/^body\./, '');
  const target = lineErrorTarget(stripped);
  if (target)
    return target.index === position ? linePartPath(target.rest, customizationKeys) : null;
  return linePartPath(stripped, customizationKeys);
}

/** The backend's own words for the first field it refused, when it named one. */
export function firstFieldMessage(error: unknown): string | null {
  return error instanceof ApiError ? (error.details[0]?.message ?? null) : null;
}
